import {
  AI_CHAT_MAX_CONTENT,
  buildChatTicketDraft,
  type AiChatMessage,
  type AiChatResponse,
  type AiChatSource,
} from '@gb-ticket/shared-types';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { AiLiveReply } from '@/platform/components/ai-live-reply';
import { AiMarkdown } from '@/platform/components/ai-markdown';
import { MessageCircleIcon, SendIcon, XIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  storeChatTicketDraft,
  useAiChat,
  useAiStatus,
} from '@/products/helpdesk/features/ai/use-ai';
import { PORTAL_ROOT } from '@/products/helpdesk/features/portal/portal-paths';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';

type Line = {
  role: 'user' | 'assistant';
  content: string;
  sources?: AiChatSource[];
  offerTicket?: boolean;
  ticketDraft?: AiChatResponse['ticketDraft'];
};

export function PortalChatWidget() {
  const copy = useVoiceCopy();
  const status = useAiStatus();
  const chat = useAiChat();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [lines, setLines] = useState<Line[]>(() => [
    { role: 'assistant', content: copy('helpdesk.portal.chat.greeting') },
  ]);
  const listRef = useRef<HTMLDivElement>(null);

  async function send() {
    const text = input.trim();
    if (!text || chat.isPending) {
      return;
    }
    const nextLines: Line[] = [...lines, { role: 'user', content: text }];
    setLines(nextLines);
    setInput('');
    const payload: AiChatMessage[] = nextLines.slice(1).map((line) => ({
      role: line.role,
      content: line.content,
    }));
    try {
      const result = await chat.mutateAsync(payload);
      setLines((current) => [
        ...current,
        {
          role: 'assistant',
          content: result.answer,
          sources: result.sources,
          offerTicket: result.offerTicket,
          ticketDraft: result.ticketDraft,
        },
      ]);
      requestAnimationFrame(() => {
        listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
      });
    } catch (error) {
      const draft = buildChatTicketDraft(payload);
      setLines((current) => [
        ...current,
        {
          role: 'assistant',
          content:
            error instanceof ApiRequestError ? error.message : copy('helpdesk.portal.chat.offline'),
          offerTicket: true,
          ticketDraft: draft,
        },
      ]);
    }
  }

  return (
    <div className="pointer-events-none fixed right-4 bottom-4 z-50 flex flex-col items-end gap-3">
      {open && (
        <section
          id="portal-chat"
          aria-label="IT-Hilfe Chat"
          className="pointer-events-auto flex h-[min(32rem,70vh)] w-[min(24rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border bg-card shadow-lg"
        >
          <header className="flex items-start justify-between gap-2 border-b px-4 py-3">
            <div>
              <p className="font-medium">IT-Hilfe fragen</p>
              <p className="text-xs text-muted-foreground">
                {status.data?.chat.available
                  ? 'Antworten aus unseren Anleitungen'
                  : 'Suche in den Anleitungen — ausführliche KI-Antworten sind gerade nicht verfügbar'}
              </p>
            </div>
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              aria-label="Chat schließen"
              onClick={() => setOpen(false)}
            >
              <XIcon />
            </Button>
          </header>
          <div ref={listRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {lines.map((line, index) => {
              const draft = line.ticketDraft;
              return (
                <article
                  key={`${line.role}-${index}`}
                  className={cn(
                    'max-w-[90%] rounded-lg px-3 py-2 text-sm',
                    line.role === 'user'
                      ? 'ml-auto bg-primary text-primary-foreground'
                      : 'bg-muted',
                  )}
                >
                  {line.role === 'assistant' ? (
                    <AiMarkdown text={line.content} />
                  ) : (
                    <p className="whitespace-pre-wrap">{line.content}</p>
                  )}
                  {line.sources && line.sources.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {line.sources.map((source) => (
                        <li key={source.slug}>
                          <Link
                            to={`${PORTAL_ROOT}/wissen/${source.slug}`}
                            className={cn(
                              'underline',
                              line.role === 'user' ? 'text-primary-foreground' : 'text-primary',
                            )}
                          >
                            {source.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  {line.offerTicket && draft ? (
                    <Button asChild size="sm" className="mt-3">
                      <Link
                        to={`${PORTAL_ROOT}/tickets/neu?aus=chat`}
                        onClick={() => {
                          storeChatTicketDraft(draft);
                          setOpen(false);
                        }}
                      >
                        Anfrage an die IT stellen
                      </Link>
                    </Button>
                  ) : null}
                </article>
              );
            })}
            {chat.isPending ? <AiLiveReply active text="" /> : null}
          </div>
          <form
            className="flex gap-2 border-t p-3"
            onSubmit={(event) => {
              event.preventDefault();
              void send();
            }}
          >
            <label className="sr-only" htmlFor="portal-chat-input">
              {copy('helpdesk.portal.chat.question')}
            </label>
            <textarea
              id="portal-chat-input"
              rows={2}
              maxLength={AI_CHAT_MAX_CONTENT}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Wobei können wir helfen?"
              className="min-h-12 flex-1 resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void send();
                }
              }}
            />
            <Button
              type="submit"
              size="icon"
              disabled={chat.isPending || input.trim().length === 0}
              aria-label="Frage senden"
            >
              <SendIcon />
            </Button>
          </form>
        </section>
      )}
      <Button
        type="button"
        className="pointer-events-auto h-12 rounded-full px-4 shadow-md"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-controls="portal-chat"
      >
        <MessageCircleIcon />
        {open ? 'Chat schließen' : 'IT-Hilfe fragen'}
      </Button>
    </div>
  );
}
